import { useAppSelector } from "@web-solutions/store/types";
import React from "react";
import { useSelector } from "react-redux";

import classNames from "classnames";

import { LString, t, tm, TM } from "@web-solutions/module-localization";
import { LocalizedString } from "@web-solutions/core/constants/remote-config";

import { remoteConfigSelector } from "core/store/remote-config/selectors";
import { PriceAfterTrialType } from "core/payment/constants";
import { ProductDetails } from "core/store/billing/selectors";
import TrialsLeft, {
  TrialsLeftType,
} from "core/payment/components/trials-left";

import { DiscountWithTimer } from "../discount-with-timer";
import { SmallBadge } from "../small-badge";
import { SoldLine } from "../sold-line";

import classes from "./style.module.scss";
import { useRemoteConfig } from "@web-solutions/base-app/hooks/use-remote-config";
import getLocale from "core/localization/get-locale";

const tKey = "core.plans";

interface ItemProps {
  isActive: boolean;
  product: ProductDetails;
  onClick: (product: ProductDetails) => void;
  titleText: string;
  subTitleText?: LocalizedString | LString;
  perText: string;
  perTitle?: string;
}

export const Item: React.FC<ItemProps> = ({
  isActive,
  product,
  onClick,
  titleText,
  subTitleText,
  perText,
  perTitle,
}) => {
  const {
    weekPrice,
    priceText,
    period,
    isTrial,
    textLineThrough,
    accentTextLineThrough,
    sold,
    promocode,
    trialsLeftBadge,
    perWeekTitle,
    dayPriceAmount,
    perWeekText,
    title,
    subTitle,
  } = product;

  const locale = getLocale();
  const { priceAfterTrialType } = useSelector(remoteConfigSelector);
  const promocodeActivated = useSelector(
    (state: any) => state.billing.promocodeActivated
  );
  const afterTrialOptions = {
    ...product,
    price:
      priceAfterTrialType === PriceAfterTrialType.WEEK
        ? `${weekPrice}/${t(`core.payment_popup.price_texts.P1W`)}`
        : priceText,
    interpolation: { escapeValue: false },
  };

  // The after-trial row is driven by the config `afterTrialText`: "Then" is a
  // hardcoded prefix and a blank value (configs use " " to switch it off) hides
  // the row. It cannot be gated on `isTrial` — that comes from the billing API
  // (`trial_period_days > 0`) and stays false for intro-priced products and for
  // any product whose price didn't resolve, which silently dropped a configured
  // line. The localized fallback already contains "Then", so it is used as-is.
  const configAfterTrialText = tm(
    product.afterTrialText,
    "",
    afterTrialOptions
  ).trim();
  const afterTrialText = configAfterTrialText
    ? `Then ${configAfterTrialText}`
    : isTrial
      ? tm(undefined, `${tKey}.after_trial.${period}`, afterTrialOptions)
      : "";

  const handleItemClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    onClick(product);
  };

  const isSoldOut = !!sold?.total && !sold.left;

  const isLightColorPerWeek = !!accentTextLineThrough && !isActive;
  // @ts-ignore
  return (
    <div
      className={classNames(classes.wrap, { [classes.disabled]: isSoldOut })}
    >
      {trialsLeftBadge && <TrialsLeft type={TrialsLeftType.BADGE_PRODUCT} />}
      <DiscountWithTimer
        discount={product.discount}
        // @ts-ignore
        badgeTitle={product.badgeTitle?.[locale]}
        centered={product.badgeCentered}
        disabled={isSoldOut}
      />
      <button
        type="button"
        onClick={handleItemClick}
        className={classNames(classes.container, {
          [classes.isActiveContainer]: isActive,
        })}
        disabled={isSoldOut}
      >
        <div className={classes.detailWrap}>
          <p className={classes.productTitle}>
            {title?.[locale] ?? titleText}

            <SmallBadge texts={product.smallBadges} disabled={isSoldOut} />
          </p>
          {!sold?.hiddenSubTitles ? (
            <>
              <p className={classes.productSubTitle}>
                <TM k={subTitle} defaultKey="" tOptions={product} />
                {/*{subTitle}000*/}
                &nbsp;
                {textLineThrough && (
                  <span className={classes.lineThrough}>
                    {tm(textLineThrough, "", product)}
                  </span>
                )}
              </p>
              {!!afterTrialText && (
                <p className={classes.afterTrialText}>{afterTrialText}</p>
              )}
            </>
          ) : null}
          {sold?.total ? (
            <SoldLine isActive={isActive} left={sold.left} total={sold.total} />
          ) : null}
        </div>
        <div
          className={classNames(classes.accentWrap, {
            [classes.isActiveAccentWrap]: isActive,
          })}
        >
          {promocodeActivated ? (
            <div className={classes.promo_badge}>
              {t(`${tKey}.promo.discount`, { discount: promocode?.discount })}
            </div>
          ) : accentTextLineThrough ? (
            <span className={classes.accentTextLineThrough}>
              {tm(accentTextLineThrough, "", product)}
            </span>
          ) : null}
          <p
            className={classNames(
              classes.accentTitle,
              isLightColorPerWeek && classes.lightAccentTitle
            )}
          >
            {/* {dayPriceAmount && perWeekTitle} */}
            <TM
              k={perWeekTitle}
              defaultKey=""
              tOptions={product}
            />
          </p>
          <p
            className={classNames(
              classes.accentSubTitle,
              isLightColorPerWeek && !isActive && classes.lightAccentSubTitle
            )}
          >
            {perWeekText[locale]}
            {/* {title?.[locale] ?? titleText} */}
          </p>
        </div>
      </button>
    </div>
  );
};
